import React, { useEffect, useMemo, useRef, useState } from 'react';
import VideoService from '../../../../../../service/VideoService';
import { CustomButton } from '../../../../../../components/Button/Button';
import styles from './TextPanels.module.scss';
import Icon from '../../../../../../components/Icon/Icon';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import Spinner from '../../../../../../../common/components/Spinner';
import { ToastNotification } from '../../../../../../components/Toast/Toast';
import useWindowSize from '../../../../../../hooks/useWindowSize';
import { EXAMPLE_BOTTOM_TEXT, EXAMPLE_TOP_TEXT, PANEL_TYPE } from '../../constants';
import { useUserContext } from '../../../../../../context/UserContext';
import { prepareTextPanelsList } from '../../utils';

const TextPanels = ({ handleOpenEditor, companyId }) => {
  const [textPanelsList, setTextPanelsList] = useState([]);
  const [selectedTextPanelId, setSelectedTextPanelId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const { height } = useWindowSize();

  const textPanelsListRef = useRef(null);
  const { updateUserAppConfig } = useUserContext();

  const textPanelsListStyles = useMemo(() => {
    if (!textPanelsListRef.current) {
      return {};
    }

    const { top } = textPanelsListRef.current.getBoundingClientRect();
    const maxHeight = window.innerHeight - top - 62;
    return { maxHeight };
  }, [height, textPanelsListRef.current]);

  const { t } = useTranslation();

  const getTextPanelsList = async () => {
    try {
      setIsLoading(true);
      const result = await VideoService.getTextPanels(companyId);
      if (result.data) {
        const preparedList = await prepareTextPanelsList(result.data);

        setTextPanelsList(preparedList);

        const defaultTextPanel = preparedList.find(({ active }) => active);
        setSelectedTextPanelId(defaultTextPanel?.id);
      }
    } catch (err) {
      console.log(err);
    }

    setIsLoading(false);
  };

  const handleChangeDefaultTextPanel = async textPanel => {
    try {
      setIsLoading(true);
      await VideoService.updateTextPanel(textPanel.id, { active: true }, companyId);

      ToastNotification({
        message: t('company.settings.text_panels.change_default.success'),
        iconName: 'success',
      });

      await getTextPanelsList();
      updateUserAppConfig();
    } catch (err) {
      console.log(err);
      ToastNotification({
        message: t('company.settings.text_panels.change_default.error'),
      });
    }

    setIsLoading(false);
  };

  const handleDeleteTextPanel = async textPanelId => {
    try {
      const isConfirm = confirm(t('company.settings.text_panels.delete.confirmation'));

      if (!isConfirm) {
        return;
      }
      setIsLoading(true);
      await VideoService.deleteTextPanel(textPanelId, companyId);

      ToastNotification({
        message: t('company.settings.text_panels.delete.success'),
        iconName: 'success',
      });

      await getTextPanelsList();
      updateUserAppConfig();
    } catch (err) {
      console.log(err);
      ToastNotification({
        message: t('company.settings.text_panels.delete.error'),
      });
    }

    setIsLoading(false);
  };

  const handleDuplicateTextPanel = async textPanelId => {
    try {
      setIsLoading(true);

      const textPanelData = await VideoService.getTextPanel(textPanelId, companyId);

      if (textPanelData) {
        const originalPanel = textPanelData;

        const { spacing, marginX, marginY, position, title, type, bgColor, textPadding } = originalPanel;

        let top, bottom;

        if (type === 0) { // ONE_PANEL type
          top = { ...originalPanel.labelTop };
          bottom = { ...originalPanel.labelBottom };

          if (top?.font) {
            top.fontId = top.font.id;
          }

          if (bottom?.font) {
            bottom.fontId = bottom.font.id;
          }

          top.backgroundColor = bgColor;
          top.textPadding = textPadding;
        } else { // TWO_PANELS type
          top = { ...originalPanel.panelTop };
          bottom = { ...originalPanel.panelBottom };

          if (top?.font) {
            top.fontId = top.font.id;
          }

          if (bottom?.font) {
            bottom.fontId = bottom.font.id;
          }
        }

        const duplicateTitle = `${title} - Copy`;

        const payload = {
          title: duplicateTitle,
          type,
          position,
          marginX,
          marginY,
          spacing,
          panelTop: top,
          panelBottom: bottom,
          default: false
        };

        await VideoService.createTextPanel(payload, companyId);

        ToastNotification({
          message: t('company.settings.text_panels.duplicate.success'),
          iconName: 'success',
        });

        await getTextPanelsList();
        updateUserAppConfig();
      }
    } catch (err) {
      console.log(err);
      ToastNotification({
        message: t('company.settings.text_panels.duplicate.error'),
      });
    }

    setIsLoading(false);
  };

  useEffect(() => {
    getTextPanelsList();
  }, [companyId]);

  return (
    <div className={styles.mainContainer}>
      <div className={styles.headerContainer}>
        <span className={styles.textPanelsHeader}>
          {t('company.settings.text_panels.all_text_panels')} ({textPanelsList.length})
        </span>
        <CustomButton
          label={t('company.settings.text_panels.new_text_panel')}
          variant="primary"
          onClick={() => handleOpenEditor('new')}
          disabled={isLoading}
          className={styles.newTextPanelBtn}
        />
      </div>
      <div
        ref={textPanelsListRef}
        style={{ ...textPanelsListStyles }}
        className={cx(styles.textPanelsListContainer, { [styles.textPanelsListDisabled]: isLoading })}
      >
        {textPanelsList.map((textPanel) => (
          <div
            key={textPanel?.id}
            className={cx(styles.textPanelItem, { [styles.textPanelItemActive]: textPanel?.id === selectedTextPanelId })}
          >
            <div className={styles.textPanelContainer} onClick={() => handleChangeDefaultTextPanel(textPanel)}>
              {textPanel?.id === selectedTextPanelId ? (
                <Icon src="companySettingCheckbox" className={styles.selectedIcon} />
              ) : (
                <div className={styles.unselectedTextPanel} />
              )}
              <div className={styles.textPanelTitle}>{textPanel?.title}</div>
              <div className={styles.textPanelTextContainer} style={textPanel.containerStyles}>
                <div style={textPanel.topLabelStyles}>{EXAMPLE_TOP_TEXT}</div>
                <div style={textPanel.bottomLabelStyles}>{EXAMPLE_BOTTOM_TEXT}</div>
              </div>
            </div>
            <div className={styles.textPanelActionContainer}>
              <CustomButton variant="link" onClick={() => handleOpenEditor(textPanel?.id)} className={styles.actionBtn} ariaLabel={t('accessibility.pencil')}>
                <Icon src="pencil" className={styles.actionIcon} />
              </CustomButton>
              <CustomButton variant="link" onClick={() => handleDuplicateTextPanel(textPanel?.id)} className={styles.actionBtn} ariaLabel={t('accessibility.duplicate')}>
                <Icon src="duplicate_generic" className={styles.actionIcon} />
              </CustomButton>
              <CustomButton variant="link" onClick={() => handleDeleteTextPanel(textPanel?.id)} className={styles.binActionBtn} ariaLabel={t('accessibility.bin')}>
                <Icon src="bin_red" className={styles.binActionIcon} />
              </CustomButton>
            </div>
          </div>
        ))}
        {isLoading && <Spinner width="40px" height="40px" size="4px" className={styles.spinner} />}
      </div>
    </div>
  );
};

export default TextPanels;
